// src/tools/measurement/areaTool.js
import maplibregl from 'maplibre-gl';
import { area as turfArea, length as turfLength, centerOfMass } from '@turf/turf';

export const areaTool = {
  _startDrawing: null,
  _stopDrawing: null,
  _onDrawComplete: null,
  _clearDrawing: null,
  _map: null,
  _isHandling: false, // Флаг для предотвращения рекурсии
  _popups: [], // Храним ссылки на попапы
  _focusedFeature: null, // Для измерения существующего объекта

  initialize: (map, options) => {
    console.log('Area tool initialized');
    areaTool._isHandling = false;
    areaTool._map = map;
    if (options.drawingControls) {
      areaTool._startDrawing = options.drawingControls.startDrawing;
      areaTool._stopDrawing = options.drawingControls.stopDrawing;
      areaTool._onDrawComplete = options.drawingControls.onDrawComplete;
      areaTool._clearDrawing = options.drawingControls.clearDrawing;
    }
    areaTool._focusedFeature = options.focusedFeature;
  },
  activate: () => {
    console.log('Area tool activated');
    areaTool._isHandling = false; // Сбрасываем флаг при активации

    // Если инструмент активирован во время редактирования объекта, измеряем его
    if (areaTool._focusedFeature && areaTool._focusedFeature.geometry.type === 'Polygon') {
        areaTool.handleDrawComplete(areaTool._focusedFeature);
        areaTool._stopDrawing?.(); // Останавливаем любой активный режим рисования
        // Не запускаем режим рисования, так как мы уже измерили существующий объект
        return;
    }

    // Устанавливаем обработчик завершения рисования
    areaTool._onDrawComplete?.(areaTool.handleDrawComplete);
    // Для измерения площади рисуем полигон
    areaTool._startDrawing?.('polygon');    
  },
  deactivate: () => {
    console.log('Area tool deactivated');
    // Сбрасываем обработчик
    areaTool.cleanup();
    areaTool._onDrawComplete?.(null);
    areaTool._stopDrawing?.();
    areaTool._focusedFeature = null;
  },
  cleanup: () => {
    // Копируем массив, чтобы избежать рекурсивного вызова при удалении.
    const popupsToRemove = [...areaTool._popups];
    areaTool._popups = [];
    popupsToRemove.forEach(p => p.remove());
  },
  handleDrawComplete: (feature) => {
    if (areaTool._isHandling) return;
    areaTool._isHandling = true;
    
    // Если это существующий объект, не выходим из режима рисования
    const isExistingFeature = !!areaTool._focusedFeature;
    if (!isExistingFeature) areaTool._stopDrawing?.();
    
    const coords = feature.geometry.coordinates[0];
    if (coords.length < 4) return; // Должно быть как минимум 3 точки + замыкающая

    // Создаем попапы для каждого сегмента
    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = coords[i];
      const p2 = coords[i+1];
      const segment = { type: 'LineString', coordinates: [p1, p2] };
      const segmentLength = turfLength(segment, { units: 'meters' });

      const midPoint = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        anchor: 'bottom',
      })
      .setLngLat(midPoint)
      .setHTML(`<div style="background: white; padding: 2px 5px; border-radius: 4px; font-size: 12px;">${segmentLength.toFixed(1)} м</div>`)
      .addTo(areaTool._map);
      areaTool._popups.push(popup);
    }

    // Вычисляем и показываем площадь
    const calculatedArea = turfArea(feature); // в кв. метрах
    const center = centerOfMass(feature);

    let areaText = `${calculatedArea.toFixed(1)} м²`;
    if (calculatedArea > 10000) {
        areaText = `${(calculatedArea / 10000).toFixed(2)} га`;
    }

    const areaPopup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: false,
    })
    .setLngLat(center.geometry.coordinates)
    .setHTML(`<div style="background: #333; color: white; padding: 5px 10px; border-radius: 4px; font-size: 14px;"><b>Площадь:</b><br>${areaText}</div>`)
    .addTo(areaTool._map);
    areaTool._popups.push(areaPopup);

    // Добавляем обработчик на закрытие главного попапа
    areaPopup.on('close', () => {
        areaTool.cleanup(); // Удаляем все попапы
        // Если это было измерение нового объекта (не из режима редактирования), очищаем и перезапускаем
        if (!isExistingFeature) {
            areaTool._clearDrawing?.(); // Удаляем нарисованную геометрию
            areaTool._isHandling = false;
            areaTool._startDrawing?.('polygon');
        } else {
            // Если измеряли существующий, просто сбрасываем флаг
            areaTool._isHandling = false;
        }
    });
  }
};